import moment from 'moment';
import { GPS_KONDITIONEN_DEFAULTS, GPS_PREISE_DEFAULTS, GPS_STAMMDATEN_DEFAULTS } from './gpsFundDefaults';

/** Section headers per PVM Stammdaten CSV (column B). */
export const PVM_LEFT_SECTION_HEADERS = {
  fondsdetails: 'Fondsdetails',
  konditionen: 'Konditionen',
  fondspartner: 'Fondspartner',
} as const;

export type PvmKeyValueRow = { label: string; value: string };

/** Placeholder when NAV / AUM cannot be resolved from stored fields */
const PVM_NUMERIC_FALLBACK = 'k. A.';

function unwrapScalar(raw: unknown): unknown {
  if (raw == null) return raw;
  if (typeof raw !== 'object') return raw;
  if (raw instanceof Date) return raw;
  const o = raw as Record<string, unknown>;
  if (o.$numberDecimal != null) return o.$numberDecimal;
  if (o.value != null && typeof o.value !== 'object') return o.value;
  if (o.amount != null && typeof o.amount !== 'object') return o.amount;
  if (typeof (raw as { toString?: () => string }).toString === 'function') {
    const s = String(raw);
    if (s && s !== '[object Object]') return s;
  }
  return undefined;
}

function toFiniteNumber(raw: unknown): number | null {
  const v = unwrapScalar(raw);
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim().replace(/\s/g, '').replace(/eur/gi, '');
  if (!s || /^nan$/i.test(s) || /object\s*object/i.test(s)) return null;
  let n: number;
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) {
    n = Number(s.replace(/\./g, '').replace(',', '.'));
  } else if (/^\d+,\d+$/.test(s)) {
    n = Number(s.replace(',', '.'));
  } else {
    n = Number(s.replace(/,/g, ''));
  }
  return Number.isFinite(n) ? n : null;
}

function fmtDate(raw: unknown): string {
  if (!raw) return '';
  const v = unwrapScalar(raw);
  const m = moment(v as string | Date);
  return m.isValid() ? m.format('DD.MM.YYYY') : String(v ?? '');
}

function fmtFondsvolumen(raw: unknown): string {
  const n = toFiniteNumber(raw);
  if (n == null) {
    const v = unwrapScalar(raw);
    if (typeof v === 'string' && v.trim() && !/nan|object\s*object/i.test(v)) {
      return /eur/i.test(v) ? v.trim() : `${v.trim()} EUR`;
    }
    return PVM_NUMERIC_FALLBACK;
  }
  return `${Math.floor(n).toLocaleString('de-DE')} EUR`;
}

function fmtPrice(raw: unknown): string {
  const n = toFiniteNumber(raw);
  if (n == null) {
    const v = unwrapScalar(raw);
    if (typeof v === 'string' && v.trim() && !/nan|object\s*object/i.test(v)) {
      const s = v.trim();
      return /eur/i.test(s) ? s : `${s.replace(/\./g, ',')} EUR`;
    }
    return PVM_NUMERIC_FALLBACK;
  }
  return `${n.toFixed(2).replace('.', ',')} EUR`;
}

/** Build clustered left-column rows for PVM page 1 (CSV column B layout). */
export function buildPvmLeftColumnClusters(pdf: Record<string, unknown>): {
  fondsdetails: PvmKeyValueRow[];
  konditionen: PvmKeyValueRow[];
  fondspartner: PvmKeyValueRow[];
} {
  const st: Record<string, unknown> = {
    ...GPS_STAMMDATEN_DEFAULTS,
    ...((pdf.stammdaten as Record<string, unknown>) || {}),
  };
  const preise: Record<string, unknown> = {
    ...GPS_PREISE_DEFAULTS,
    ...((pdf.preise as Record<string, unknown>) || {}),
  };
  const kond: Record<string, unknown> = {
    ...GPS_KONDITIONEN_DEFAULTS,
    ...((pdf.konditionen as Record<string, unknown>) || {}),
  };

  const nav = preise.ruecknahmepreis;

  const fondsdetails: PvmKeyValueRow[] = [
    { label: 'Kategorie:', value: String(st.fondsart || '') },
    { label: 'WKN:', value: String(st.wkn || '') },
    { label: 'ISIN:', value: String(st.isin || '') },
    { label: 'Fondsdomizil:', value: String(st.fondsdomizil || 'Luxemburg') },
    { label: 'Fondswährung:', value: String(preise.wahrung || 'EUR') },
    { label: 'Fondsvermögen:', value: fmtFondsvolumen(preise.fondsvolumen) },
    { label: 'Kurs (NAV):', value: fmtPrice(nav) },
    { label: 'Ertragsverwendung:', value: String(preise.ertragsart || '') },
    { label: 'Auflagedatum:', value: fmtDate(st.auflagedatum) },
    { label: 'Geschäftsjahresende:', value: String(st.geschäftsjahr || '') },
    { label: 'Fondstyp/Rechtsform:', value: String(st.fondstyp || '') },
    { label: 'Risiko-/Ertragsprofil (SRI 1-7)*:', value: String(st.srri || '').replace(' von 7', '') || '3' },
    { label: 'Einstufung nach SFDR:', value: String(st.nachhaltigkeit || st.sfdr || '') },
  ].filter((r) => r.value.trim());

  /** Footnote ² belongs on the TER label — dynamic Axion value must stay suffix-free. */
  const stripTerFootnote = (v: string) => v.replace(/\s*²\s*$/u, '').trim();

  const konditionen: PvmKeyValueRow[] = [
    { label: 'Verwaltungsgebühr:', value: String(kond.verwaltungsgebühr || '') },
    { label: 'Erfolgshonorar:', value: String(kond.performance_fee || '') },
    {
      label: 'TER²:',
      value: stripTerFootnote(String(kond.ter_2022 || kond.laufendeKosten || '')),
    },
    { label: 'Ausgabeaufschlag:', value: String(kond.ausgabeaufschlag || '') },
  ].filter((r) => r.value.trim());

  const kontakt =
    String(st.kontakt || '').trim() ||
    'PARTNERS VermögensManagement AG\nSüdliche Auffahrtsallee 37\n80639 München\nTel. +49(0)89 24 21 39 0\ninfo@pvm-ag.de';

  const normalizeCompany = (v: string) =>
    v.replace(/Vermögensmanagement/g, 'VermögensManagement');

  const fondspartner: PvmKeyValueRow[] = [
    { label: 'Kapitalverwaltungs-\ngesellschaft:', value: String(st.verwaltungsgesellschaft || '') },
    { label: 'Verwahrstelle:', value: String(st.verwahrstelle || '') },
    { label: 'Fondsberater:', value: normalizeCompany(String(st.anlageberater || '')) },
    { label: 'Kontakt:', value: normalizeCompany(kontakt) },
  ].filter((r) => r.value.trim());

  return { fondsdetails, konditionen, fondspartner };
}
